import type { PendingRealtimeQuestion } from '@/convex/voice/realtime_runtime_types.helpers';
import { boundedRealtimeVoiceContextText } from '@/lib/realtime-voice-context-text';

const MAX_QUESTION_CHARS = 300;
const MAX_TITLE_CHARS = 100;
const MAX_OPTION_LABEL_CHARS = 80;
const MAX_OPTION_VALUE_CHARS = 128;
const MAX_OPTIONS = 8;
const MAX_TOOL_CALL_ID_CHARS = 128;

export type RealtimeVoiceOffPageQuestion =
  | {
      kind: 'single';
      toolCallId: string;
      question: string;
      questionType?: string;
      required: boolean;
      options?: Array<{ label: string; value: string }>;
    }
  | {
      kind: 'open-chat';
      toolCallId?: string;
      title?: string;
      reason: 'group' | 'missing-call-id' | 'too-many-options' | 'unsupported';
    };

function validToolCallId(value: string | undefined): value is string {
  return (
    !!value &&
    value.length <= MAX_TOOL_CALL_ID_CHARS &&
    /^[A-Za-z0-9_-]+$/.test(value)
  );
}

/** Project only facts that the floating call can safely act on. */
export function projectRealtimeVoiceOffPageQuestion(
  pendingQuestions: readonly PendingRealtimeQuestion[]
): RealtimeVoiceOffPageQuestion | null {
  const pending = pendingQuestions[0];
  if (!pending) return null;

  const toolCallId = validToolCallId(pending.toolCallId)
    ? pending.toolCallId
    : undefined;
  if (pending.kind === 'group') {
    return {
      kind: 'open-chat',
      reason: 'group',
      ...(toolCallId ? { toolCallId } : {}),
      ...(boundedRealtimeVoiceContextText(pending.title, MAX_TITLE_CHARS)
        ? {
            title: boundedRealtimeVoiceContextText(
              pending.title,
              MAX_TITLE_CHARS
            ),
          }
        : {}),
    };
  }
  if (!toolCallId) return { kind: 'open-chat', reason: 'missing-call-id' };
  if (
    pending.requiresApprovalPreview ||
    (pending.questionType === 'confirm' && pending.helperText?.trim())
  ) {
    return { kind: 'open-chat', toolCallId, reason: 'unsupported' };
  }
  if ((pending.options?.length ?? 0) > MAX_OPTIONS) {
    return { kind: 'open-chat', toolCallId, reason: 'too-many-options' };
  }
  const question = boundedRealtimeVoiceContextText(
    pending.question,
    MAX_QUESTION_CHARS
  );
  if (!question || pending.question.trim().length > MAX_QUESTION_CHARS) {
    return { kind: 'open-chat', toolCallId, reason: 'unsupported' };
  }

  const options = pending.options?.map((option) => ({
    label:
      boundedRealtimeVoiceContextText(option.label, MAX_OPTION_LABEL_CHARS) ??
      '',
    value: option.value,
  }));
  if (
    options?.some(
      (option) =>
        !option.label ||
        !option.value ||
        option.value.length > MAX_OPTION_VALUE_CHARS
    )
  ) {
    return { kind: 'open-chat', toolCallId, reason: 'unsupported' };
  }

  return {
    kind: 'single',
    toolCallId,
    question,
    ...(pending.questionType
      ? { questionType: pending.questionType.slice(0, 32) }
      : {}),
    required: pending.required !== false,
    ...(options?.length ? { options } : {}),
  };
}

/** Transient voice prompt. It never treats the viewed page as the answer owner. */
export function buildRealtimeVoiceOffPageQuestionPrompt(
  pending: RealtimeVoiceOffPageQuestion
): string {
  if (pending.kind === 'open-chat') {
    return 'A question form is waiting in the original chat. Ask the user to open that chat to complete it. Do not call answer_current_question for this form while they are away from the chat.';
  }
  return [
    'The original voice chat has one pending question. Ask it briefly. Use answer_current_question with the exact toolCallId when the user answers.',
    JSON.stringify(pending),
  ].join('\n');
}
